'use client';

import { useEffect, useRef } from 'react';
import type { PortalUser } from '@/lib/user-portal/types';
import type { BadgeAwardEntry, LevelAwardEntry, PointsAwardEntry } from '@/lib/gamification/types';
import { useCelebration } from './celebration-provider';
import type { CelebrationEvent } from './celebration-events';

interface SnapshotShape {
  totalPoints: number;
  rank: number;
  badgeCount: number;
  riskLevel: PortalUser['riskLevel'];
  timesCompromised: number;
}

interface DetectorInput {
  user: PortalUser | null;
  pointsAwards?: PointsAwardEntry[];
  badgeAwards?: BadgeAwardEntry[];
  levelAwards?: LevelAwardEntry[];
  /**
   * False when the tenant has Gamification switched off (the portal's
   * gamification routes answer 403 module_disabled, audit G17): no rank,
   * points, badge or level celebration. The user's rank and totals are then
   * placeholders (an empty leaderboard ranks everyone #1), so the snapshot
   * keeps the last real ones and switching Gamification back on celebrates
   * nothing that did not happen.
   */
  gamificationEnabled?: boolean;
}

export function useCelebrationDetector({
  user,
  pointsAwards = [],
  badgeAwards = [],
  levelAwards = [],
  gamificationEnabled = true,
}: DetectorInput) {
  const { enqueue } = useCelebration();
  const fired = useRef(false);

  useEffect(() => {
    if (!user || fired.current) return;
    fired.current = true;

    const snapKey = `portal:celebrations:snapshot:${user.id}`;
    let prev: SnapshotShape | null = null;
    try {
      const raw = localStorage.getItem(snapKey);
      if (raw) prev = JSON.parse(raw);
    } catch {}

    const next: SnapshotShape = {
      totalPoints: gamificationEnabled ? user.totalPoints : (prev?.totalPoints ?? user.totalPoints),
      rank: gamificationEnabled ? user.rank : (prev?.rank ?? user.rank),
      badgeCount: gamificationEnabled ? user.badgeCount : (prev?.badgeCount ?? user.badgeCount),
      riskLevel: user.riskLevel,
      timesCompromised: user.timesCompromised,
    };

    const events: CelebrationEvent[] = [];

    if (prev) {
      if (!gamificationEnabled) {
        // No rank event: the rank is a placeholder, not a move.
      } else if (next.rank < prev.rank) {
        events.push({
          id: `rank-up-${user.id}-${prev.rank}-${next.rank}`,
          kind: 'rank_climbed',
          rank: next.rank,
          delta: prev.rank - next.rank,
        });
      } else if (next.rank - prev.rank >= 2) {
        events.push({
          id: `rank-down-${user.id}-${prev.rank}-${next.rank}`,
          kind: 'rank_dropped',
          rank: next.rank,
          delta: next.rank - prev.rank,
        });
      }

      if (next.timesCompromised > prev.timesCompromised) {
        events.push({
          id: `compromised-${user.id}-${next.timesCompromised}`,
          kind: 'phishing_compromised',
          // No campaign on the snapshot: the copy names it "a recent
          // simulation" in the learner's language (describeCelebration).
          campaignName: '',
        });
      }

      const riskOrder = { Low: 0, Medium: 1, High: 2 } as const;
      if (riskOrder[next.riskLevel] > riskOrder[prev.riskLevel]) {
        events.push({
          id: `risk-${user.id}-${prev.riskLevel}-${next.riskLevel}`,
          kind: 'risk_level_increased',
          level: next.riskLevel,
        });
      }
    }

    // Points, badges and levels are gamification: none when it is switched off.
    // An award is the learner's when its row names them — by RECIPIENT ID,
    // which every ledger row carries and the portal session knows (user.id).
    // Matched by e-mail, nothing fired for a real learner: the employee
    // portal's rows are the learner's own ledger rows and none carries an
    // e-mail, so only the admin previews, whose rows the API enriches with
    // one, ever celebrated (lead item A, 2026-09-26). A row that names no one
    // is nobody's.
    const isMine = (award: { recipientId?: string }) =>
      gamificationEnabled && !!award.recipientId && award.recipientId === user.id;

    const userAwards = pointsAwards.filter(isMine);
    userAwards.slice(0, 3).forEach((p) => {
      if (p.points < 25) return;
      events.push({
        id: `points-${p.id}`,
        kind: 'points_awarded',
        points: p.points,
        activity: p.activity,
        totalPoints: user.totalPoints,
      });
    });

    badgeAwards
      .filter(isMine)
      .slice(0, 3)
      .forEach((b) => {
        events.push({
          id: `badge-${b.id}`,
          kind: 'badge_earned',
          badgeName: b.badgeName,
          badgeIcon: b.badgeIcon,
        });
      });

    levelAwards
      .filter(isMine)
      .slice(0, 1)
      .forEach((l) => {
        events.push({
          id: `level-${l.id}`,
          kind: 'level_up',
          levelName: l.levelName,
          levelIcon: l.levelIcon,
          pointsRequired: l.pointsRequired,
        });
      });

    if (events.length) enqueue(events);

    try {
      localStorage.setItem(snapKey, JSON.stringify(next));
    } catch {}
  }, [user, pointsAwards, badgeAwards, levelAwards, gamificationEnabled, enqueue]);
}
